/**
 * Pure rules for the task drawer's edit and create forms (DD-017, DD-051):
 * the draft, what Save sends, when Save and Create are allowed, and the
 * owner, tag and client options. On the agency Tasks tab (DD-049) create
 * mode also asks for the client, and the people it offers belong to it.
 * Tags follow TD-103: the owner counts as tagged but is never sent as a tag.
 */

import type { DropdownOption } from "@/components/ui/OptionDropdown";
import type { Client } from "@/types";
import { assigneePillStyle } from "../taskAssigneeConfig";
import type {
  CreateTaskRequest,
  Task,
  TaskAssignee,
  TaskAssigneeOption,
  TaskLinkRef,
  TaskPerson,
  UpdateTaskRequest,
} from "../types";

/** The form's fields as typed. `clientId` is the task's client in edit
 * mode, and the list's or the picked client in create mode. `people` are
 * the tags. They may hold the owner, whom every read leaves out. */
export interface TaskDraft {
  title: string;
  description: string;
  assigneeId: string | null;
  clientId: string | null;
  people: TaskPerson[];
}

/** Assignee dropdown value for Team (no assignee). Maps to assigneeId null. */
export const TEAM_VALUE = "__team__";

/** A client the Tasks tab's client field can offer. */
export type TaskClientOption = Pick<Client, "id" | "clientName" | "status">;

export function draftFromTask(task: Task): TaskDraft {
  return {
    title: task.title,
    description: task.description ?? "",
    assigneeId: task.assigneeId,
    clientId: task.clientId,
    people: task.people,
  };
}

/** A new draft. `people` start tagged: the Home panel passes the client's
 * contacts (DD-053). */
export function emptyDraft(clientId: string | null, people: TaskPerson[] = []): TaskDraft {
  return { title: "", description: "", assigneeId: null, clientId, people };
}

/** Apply a field change. People differ per client, so another client drops
 * the owner back to Team and clears the tags. In edit mode, handing the task
 * on from its saved owner keeps that owner on as a tag, as the server does
 * (TD-103), so the form shows what the save will store. */
export function changeDraft(
  draft: TaskDraft,
  patch: Partial<TaskDraft>,
  savedOwner: TaskPerson | null = null
): TaskDraft {
  const next = { ...draft, ...patch };
  if (patch.clientId !== undefined && patch.clientId !== draft.clientId) {
    next.assigneeId = null;
    next.people = [];
  }
  if (savedOwner !== null) {
    const savedId = savedOwner.id;
    const leaves = draft.assigneeId === savedId && next.assigneeId !== savedId;
    if (leaves && !next.people.some((person) => person.id === savedId)) {
      next.people = [...next.people, savedOwner];
    }
  }
  return next;
}

/** Move a saved task's draft to another client (DD-049). The team works on
 * every client, so the saved team owner and team tags stay; the old
 * client's contacts go, as the server drops them, and a dropped owner is
 * not kept on as a tag. An owner picked in this edit goes back to Team,
 * since the draft holds no kind for them. Back on the saved client, the
 * saved people return. */
export function moveDraft(draft: TaskDraft, clientId: string, saved: Task): TaskDraft {
  if (clientId === saved.clientId) {
    return { ...draft, clientId, assigneeId: saved.assigneeId, people: saved.people };
  }
  const owner = saved.assignee;
  const keepsOwner = owner !== null && owner.kind === "team" && draft.assigneeId === owner.id;
  return {
    ...draft,
    clientId,
    assigneeId: keepsOwner ? owner.id : null,
    people: draft.people.filter((person) => person.kind === "team"),
  };
}

/** The tags the form shows: every tag but the owner, who shows first. */
export function draftTags(draft: TaskDraft): TaskPerson[] {
  return draft.people.filter((person) => person.id !== draft.assigneeId);
}

/** The owner's person: one of the offered people, or the task's current owner. */
export function ownerPerson(assigneeId: string | null, candidates: (TaskPerson | null)[]): TaskPerson | null {
  if (assigneeId === null) return null;
  return candidates.find((person): person is TaskPerson => person?.id === assigneeId) ?? null;
}

/** Whether the client will see the task (TD-103): the owner or a tag is one
 * of its contacts. The drawer's edit mode shows it live. */
export function draftClientVisible(owner: TaskPerson | null, draft: TaskDraft): boolean {
  return owner?.kind === "client" || draftTags(draft).some((person) => person.kind === "client");
}

/** The people the tag picker offers: neither the owner nor a tag yet, and in
 * the portal only the client's own people (DD-053). */
export function tagPickerOptions(options: TaskPerson[], draft: TaskDraft, canTagTeam: boolean): TaskPerson[] {
  const taken = new Set(draft.people.map((person) => person.id));
  return options.filter(
    (person) =>
      person.id !== draft.assigneeId && !taken.has(person.id) && (canTagTeam || person.kind === "client")
  );
}

/** The client's contacts: the tags a task created from Home starts with (DD-053). */
export function clientContacts(people: TaskAssigneeOption[]): TaskPerson[] {
  return people.filter((person) => person.kind === "client");
}

/** The tag ids a save sends: each person once, never the owner, sorted so
 * equal sets compare equal. Without `canTagTeam` (the portal) only the
 * client's people go, and the server keeps the team's tags (TD-103). */
export function sentPersonIds(people: TaskPerson[], ownerId: string | null, canTagTeam: boolean): string[] {
  const ids = people
    .filter((person) => person.id !== ownerId && (canTagTeam || person.kind === "client"))
    .map((person) => person.id);
  return Array.from(new Set(ids)).sort();
}

/** Only the fields the draft changed, trimmed as the server stores them.
 * An edit that trims back to the saved value is no change, and an assignee
 * nobody touched is never sent: they may no longer be assignable. The tags
 * go only when they changed or the owner changed. */
export function taskEditPatch(task: Task, draft: TaskDraft, canTagTeam = true): UpdateTaskRequest {
  const patch: UpdateTaskRequest = {};
  const title = draft.title.trim();
  if (title !== task.title) patch.title = title;
  const description = draft.description.trim() || null;
  if (description !== (task.description?.trim() || null)) patch.description = description;
  if (draft.assigneeId !== task.assigneeId) patch.assigneeId = draft.assigneeId;
  if (draft.clientId !== null && draft.clientId !== task.clientId) patch.clientId = draft.clientId;
  // An owner change always sends the tags: the server would otherwise keep the
  // old owner tagged, even when the user removed that tag in the same save.
  const tags = sentPersonIds(draft.people, draft.assigneeId, canTagTeam);
  const ownerChanged = draft.assigneeId !== task.assigneeId;
  if (ownerChanged || tags.join() !== sentPersonIds(task.people, task.assigneeId, canTagTeam).join()) {
    patch.personIds = tags;
  }
  return patch;
}

/** Save is allowed once a field changed and the title is not blank. */
export function canSaveTaskDraft(task: Task, draft: TaskDraft, canTagTeam = true): boolean {
  if (!draft.title.trim()) return false;
  return Object.keys(taskEditPatch(task, draft, canTagTeam)).length > 0;
}

/** Create is allowed once the task has a title and a client. */
export function canCreateTaskDraft(draft: TaskDraft): boolean {
  return draft.title.trim().length > 0 && draft.clientId !== null;
}

/** The people a task can go to. The shot list takes no client contact
 * (DD-022), so its drawers drop them. */
export function assignablePeople(
  people: TaskAssigneeOption[],
  excludeClientContacts: boolean
): TaskAssigneeOption[] {
  return excludeClientContacts ? people.filter((person) => person.kind !== "client") : people;
}

/** The assignee to send: the pick only when that person can take tasks on
 * the chosen client (they are in its `people`), else Team (null). */
export function assigneeForClient(
  assigneeId: string | null,
  people: TaskAssigneeOption[]
): string | null {
  if (assigneeId === null) return null;
  return people.some((person) => person.id === assigneeId) ? assigneeId : null;
}

/** `POST /api/clients/<id>/tasks` for a draft, with the list's starting
 * links and the draft's tags. The server tags the creator too (DD-053). */
export function createTaskRequest(
  draft: TaskDraft,
  people: TaskAssigneeOption[],
  links: TaskLinkRef[]
): CreateTaskRequest {
  const assigneeId = assigneeForClient(draft.assigneeId, people);
  return {
    title: draft.title.trim(),
    description: draft.description.trim() || null,
    assigneeId,
    links,
    personIds: sentPersonIds(draft.people, assigneeId, true),
  };
}

/** The client field's options: every client but the archived ones, by name
 * ignoring case, then id. */
export function clientPickerOptions(clients: TaskClientOption[]): DropdownOption<string>[] {
  return clients
    .filter((client) => client.status !== "ARCHIVED")
    .map((client) => ({ value: client.id, label: client.clientName }))
    .sort(
      (a, b) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase()) ||
        a.value.localeCompare(b.value)
    );
}

/** The assignee field's options: Team (unless the viewer may not clear the
 * owner), then the people who can take tasks on the client, colored like
 * their pills. A current assignee missing from `people` (a contact whose
 * access was removed) stays listed, so the field shows who holds the task
 * instead of a blank. */
export function assigneeDropdownOptions(
  people: TaskAssigneeOption[],
  current: TaskAssignee | null,
  excludeClientContacts = false,
  canClearOwner = true
): DropdownOption<string>[] {
  const offered = assignablePeople(people, excludeClientContacts);
  const keep = current && !(excludeClientContacts && current.kind === "client") ? current : null;
  const listed = keep && !offered.some((person) => person.id === keep.id) ? [...offered, keep] : offered;
  const options = listed.map((person) => {
    const style = assigneePillStyle(person);
    return { value: person.id, label: style.label, dot: style.dot };
  });
  const team = assigneePillStyle(null);
  return canClearOwner ? [{ value: TEAM_VALUE, label: team.label, dot: team.dot }, ...options] : options;
}
